import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Trophy, 
  Award, 
  Star, 
  Medal, 
  Target, 
  Calendar,
  TrendingUp,
  BookOpen,
  Clock,
  CheckCircle,
  Zap,
  Crown
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { getStudentEnrollments } from '../../services/elearningStudentService';
import { getStudentProfileByToken } from '../../services/elearningStudentService';
import { useTenantContext } from '../../context/TenantContext';
import { useToast } from '../../hooks/useToast';
import StudentLayout from '../../components/student/StudentLayout';

interface Achievement {
  id: string;
  title: string;
  description: string;
  icon: React.ComponentType<any>;
  color: string;
  bgColor: string;
  earned: boolean;
  earnedDate?: string;
  progress?: number;
  maxProgress?: number;
  category: 'learning' | 'time' | 'completion' | 'streak' | 'social';
}

const StudentAchievementsPage: React.FC = () => {
  const navigate = useNavigate();
  const { tenant } = useTenantContext();
  const { toast } = useToast();
  
  const [studentProfile, setStudentProfile] = useState<any>(null);
  const [enrollments, setEnrollments] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState<string>('all');

  // Données des achievements simulées
  const achievements: Achievement[] = [
    {
      id: '1',
      title: 'Premier Pas',
      description: 'Terminez votre premier cours',
      icon: BookOpen,
      color: 'text-blue-500',
      bgColor: 'bg-blue-500/10',
      earned: true,
      earnedDate: '2024-01-15',
      category: 'learning'
    },
    {
      id: '2',
      title: 'Marathonien',
      description: 'Étudiez pendant 10 heures au total',
      icon: Clock,
      color: 'text-green-500',
      bgColor: 'bg-green-500/10',
      earned: true,
      earnedDate: '2024-01-20',
      category: 'time'
    },
    {
      id: '3',
      title: 'Perfectionniste',
      description: 'Obtenez 100% à un quiz',
      icon: Star,
      color: 'text-yellow-500',
      bgColor: 'bg-yellow-500/10',
      earned: false,
      progress: 85,
      maxProgress: 100,
      category: 'completion'
    },
    {
      id: '4',
      title: 'Régularité',
      description: 'Étudiez 7 jours consécutifs',
      icon: Calendar,
      color: 'text-purple-500',
      bgColor: 'bg-purple-500/10',
      earned: false,
      progress: 4,
      maxProgress: 7,
      category: 'streak'
    },
    {
      id: '5',
      title: 'Champion',
      description: 'Terminez 5 cours avec excellence',
      icon: Crown,
      color: 'text-orange-500',
      bgColor: 'bg-orange-500/10',
      earned: false,
      progress: 2,
      maxProgress: 5,
      category: 'completion'
    },
    {
      id: '6',
      title: 'Éclair',
      description: 'Terminez un cours en moins de 3 jours',
      icon: Zap,
      color: 'text-pink-500',
      bgColor: 'bg-pink-500/10',
      earned: true,
      earnedDate: '2024-01-25',
      category: 'time'
    }
  ];

  const categories = [
    { id: 'all', label: 'Tous', count: achievements.length },
    { id: 'learning', label: 'Apprentissage', count: achievements.filter(a => a.category === 'learning').length },
    { id: 'time', label: 'Temps', count: achievements.filter(a => a.category === 'time').length },
    { id: 'completion', label: 'Accomplissement', count: achievements.filter(a => a.category === 'completion').length },
    { id: 'streak', label: 'Régularité', count: achievements.filter(a => a.category === 'streak').length }
  ];

  // Callback appelé quand le profil étudiant est chargé dans le layout
  const handleStudentProfileLoaded = (profile: any) => {
    setStudentProfile(profile);
    if (profile?.id) {
      loadEnrollments(profile.id);
    }
  };

  const loadEnrollments = async (studentId: string) => {
    if (!studentId || !tenant?.id) return;

    setIsLoading(true);
    try {
      const enrollmentsData = await getStudentEnrollments(studentId, tenant.id);
      setEnrollments(enrollmentsData);
    } catch (error) {
      console.error('Erreur lors du chargement des inscriptions:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredAchievements = selectedCategory === 'all' 
    ? achievements 
    : achievements.filter(a => a.category === selectedCategory);

  const earnedCount = achievements.filter(a => a.earned).length;
  const totalPoints = earnedCount * 100; // 100 points par achievement

  return (
    <StudentLayout
      title="Mes Réussites"
      description="Découvrez vos accomplissements et débloquez de nouveaux défis"
      onStudentProfileLoaded={handleStudentProfileLoaded}
    >
      <SEOMetaTags 
        title="Mes Réussites - E-Learning"
        description="Consultez vos badges, certificats et accomplissements d'apprentissage."
        keywords="réussites, badges, certificats, accomplissements, e-learning"
      />

          {/* Statistiques */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Badges obtenus</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{earnedCount}/{achievements.length}</p>
                  </div>
                  <Trophy className="h-8 w-8 text-yellow-500" />
                </div>
                <Progress value={(earnedCount / achievements.length) * 100} className="mt-3" />
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Points totaux</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">{totalPoints}</p>
                  </div>
                  <Star className="h-8 w-8 text-purple-500" />
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Niveau</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">Débutant</p>
                  </div>
                  <Medal className="h-8 w-8 text-blue-500" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filtres par catégorie */}
          <div className="flex flex-wrap gap-2 mb-8">
            {categories.map((category) => (
              <Button
                key={category.id}
                variant={selectedCategory === category.id ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSelectedCategory(category.id)}
                className="flex items-center space-x-2"
              >
                <span>{category.label}</span>
                <Badge variant="secondary" className="ml-2">
                  {category.count}
                </Badge>
              </Button>
            ))}
          </div>

          {/* Grille des achievements */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredAchievements.map((achievement, index) => {
              const Icon = achievement.icon;
              
              return (
                <motion.div
                  key={achievement.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                >
                  <Card className={`border-0 shadow-lg transition-all duration-300 ${
                    achievement.earned 
                      ? 'bg-gradient-to-br from-white to-gray-50 dark:from-gray-800 dark:to-gray-900' 
                      : 'bg-gray-100 dark:bg-gray-800 opacity-75'
                  }`}>
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className={`p-3 rounded-lg ${achievement.bgColor}`}>
                          <Icon className={`h-6 w-6 ${achievement.color}`} />
                        </div>
                        {achievement.earned && (
                          <CheckCircle className="h-5 w-5 text-green-500" />
                        )}
                      </div>
                      
                      <h3 className="font-semibold text-lg text-gray-900 dark:text-white mb-2">
                        {achievement.title}
                      </h3>
                      
                      <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">
                        {achievement.description}
                      </p>

                      {achievement.earned ? (
                        <div className="flex items-center space-x-2">
                          <Badge variant="default" className="bg-green-500">
                            Obtenu
                          </Badge>
                          {achievement.earnedDate && (
                            <span className="text-xs text-gray-500">
                              {new Date(achievement.earnedDate).toLocaleDateString('fr-FR')}
                            </span>
                          )}
                        </div>
                      ) : (
                        <div>
                          {achievement.progress !== undefined && achievement.maxProgress && (
                            <div className="mb-2">
                              <div className="flex justify-between items-center mb-1">
                                <span className="text-xs text-gray-600 dark:text-gray-400">
                                  Progression
                                </span>
                                <span className="text-xs text-gray-600 dark:text-gray-400">
                                  {achievement.progress}/{achievement.maxProgress}
                                </span>
                              </div>
                              <Progress 
                                value={(achievement.progress / achievement.maxProgress) * 100} 
                                className="h-2" 
                              />
                            </div>
                          )}
                          <Badge variant="outline">
                            En cours
                          </Badge>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          {/* Message si aucun achievement dans la catégorie */}
          {filteredAchievements.length === 0 && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-12"
            >
              <Trophy className="h-16 w-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                Aucune réussite dans cette catégorie
              </h3>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Continuez à apprendre pour débloquer de nouveaux accomplissements !
              </p>
              <Button onClick={() => navigate('/elearning/student/courses')}>
                Voir mes cours
              </Button>
            </motion.div>
          )}
    </StudentLayout>
  );
};

export default StudentAchievementsPage;
